import React, { useMemo, useState } from "react";
import Icon from "./icons";
import Avatar from "./avatar";
import AgentSteps from "./agentSteps";
import MapChanges from "./mapChanges";
import { linkCitations, sanitizeAnswerHtml } from "../../../lib/artemis/sanitize";
import { answerSources, skillsUsed, statsLine, stepsSummary } from "../../../lib/artemis/present";
import ResultCards from "./resultCards";
import AskCard from "./askCard";
import { askQuestions, isAsking, leadIn } from "../../../lib/artemis/ask";
import { mentionSegments } from "../../../lib/artemis/group";

// One question and Artemis's reply. The latest turn shows everything: steps, the answer,
// what changed on the map, sources, feedback and follow-ups. Earlier turns fold to the
// question, a one-line summary and the answer.

export const FEEDBACK_REASONS = ['Wrong dates', 'Wrong state or unit', 'Out of date', 'Changed my map wrongly', "Didn't answer the question"]

const Answer = ({ message }) => {
  const html = useMemo(
    () => linkCitations(sanitizeAnswerHtml(message.answer), message.citations),
    [message.answer, message.citations]
  )
  const streaming = message.status === 'streaming'
  return <div className={`chat-answer artemis-answer ${streaming ? 'artemis-answer-streaming' : ''}`} dangerouslySetInnerHTML={{ __html: html }} />
}

// Which of Artemis's skills the reply used, e.g. "Method · Glassing strategy". In the ARTEMIS tab's hub
// (WEB-7170) each opens the Skills section: the user's own skill in the editor, a method with Make it mine.
// In a group, Mine is the asker's: a partner's skill reads "Jake's skill" and opens nothing here.
const MethodBadges = ({ message, skillActions, people }) => {
  const skills = skillsUsed(message)
  if (skills.length === 0) return null
  const askedByPartner = !!people && message.authorUserId != null && String(message.authorUserId) !== String(people.meId)
  const asker = askedByPartner
    ? (message.authorName || (people.members.find(m => String(m.UserId) === String(message.authorUserId)) || {}).Name || 'Partner').split(' ')[0]
    : null
  const label = s => (!s.mine ? 'Method' : asker ? `${asker}’s skill` : 'Your skill')
  const open = s => (s.mine
    ? (!asker && skillActions && skillActions.onOpenSkill && s.id != null ? () => skillActions.onOpenSkill(s.id) : null)
    : (skillActions && skillActions.onOpenMethod ? () => skillActions.onOpenMethod(s.key) : null))
  return (
    <div className="artemis-method-badges">
      {skills.map(s => {
        const onClick = open(s)
        const inner = <><Icon name="skill" size={13} /><span>{label(s)} · {s.title}</span></>
        return onClick
          ? <button key={s.key} type="button" className={`artemis-method-badge artemis-method-badge-button ${s.mine ? 'artemis-method-mine' : ''}`} onClick={onClick}>{inner}</button>
          : <span key={s.key} className={`artemis-method-badge ${s.mine ? 'artemis-method-mine' : ''}`}>{inner}</span>
      })}
    </div>
  )
}

const ReplyState = ({ message, people, onRetry, onRefresh }) => {
  if (message.status === 'saving') {
    // In a group, a partner's question: Artemis runs on their screen, and this one polls what it has written.
    const asker = people && message.authorUserId != null && String(message.authorUserId) !== String(people.meId)
      ? (message.authorName || (people.members.find(m => String(m.UserId) === String(message.authorUserId)) || {}).Name || 'a partner').split(' ')[0]
      : null
    return (
      <div className="artemis-reply-state" role="status">
        <div className="artemis-reply-state-row">
          <span className="artemis-working-dot" aria-hidden="true" />
          <span className="artemis-flex">{asker ? `Artemis is working on ${asker}’s question…` : 'Artemis is still writing this reply'}</span>
          {onRefresh && <button type="button" className="artemis-small-button" onClick={onRefresh}>Refresh</button>}
        </div>
      </div>
    )
  }
  if (message.status === 'failed') {
    return (
      <div className="artemis-reply-state artemis-reply-failed" role="status">
        <Icon name="failed" size={15} />
        <span className="artemis-flex">{message.answer ? 'This answer is incomplete.' : 'Something went wrong'}</span>
        <button type="button" className="artemis-small-button" onClick={onRetry}>Try again</button>
      </div>
    )
  }
  if (message.status === 'stopped') {
    const changed = (message.actions || []).some(a => a.outcome === 'changed')
    return (
      <div className="artemis-reply-state" role="status">
        <div className="artemis-reply-state-row">
          <Icon name="stop" size={15} />
          <span className="artemis-flex">You stopped this reply</span>
          <button type="button" className="artemis-small-button" onClick={onRetry}>Ask again</button>
        </div>
        {changed && <span className="artemis-reply-state-note">Changes made before you stopped stay on your map, each with its own Undo.</span>}
      </div>
    )
  }
  return null
}

const Sources = ({ message, onSourceFeedback }) => {
  if (message.status === 'streaming') return null
  const sources = answerSources(message)
  if (sources.length === 0) return null
  return (
    <section className="artemis-sources" aria-label="Sources">
      <span className="artemis-eyebrow">Sources</span>
      {sources.map(source => (
        <div key={source.key} className={`artemis-source ${source.disliked ? 'artemis-source-bad' : ''}`}>
          <span className="artemis-source-n">{source.n}</span>
          <span className="artemis-source-text">
            {source.url
              ? <a className="artemis-source-title" href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>
              : <span className="artemis-source-title">{source.title}</span>}
            {(source.disliked || source.sub || source.community) && (
              <span className="artemis-source-sub">
                {source.disliked ? 'Marked as a poor source' : [source.sub, source.community ? 'Community source' : null].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
          {/* Only documents can be flagged; the feedback endpoint takes a document id. */}
          {source.documentId !== undefined && (source.disliked
            ? <button type="button" className="artemis-small-button" onClick={() => onSourceFeedback(message, source, true)}>Undo</button>
            : (
              <button type="button" className="artemis-icon-button artemis-icon-button-sm" title="Not a good source" aria-label={`Not a good source: ${source.title}`}
                onClick={() => onSourceFeedback(message, source, false)}>
                <Icon name="flag" size={14} />
              </button>
            ))}
        </div>
      ))}
    </section>
  )
}

const Feedback = ({ message, onFeedback, onFeedbackComment, onCopy, onPin }) => {
  const [formOpen, setFormOpen] = useState(false)
  const [reasons, setReasons] = useState([])
  const [note, setNote] = useState('')
  const [sent, setSent] = useState(false)

  // v2 feedback goes by QueryId, v3 by the saved reply's id.
  if (message.status !== 'complete' || !(message.queryId || message.messageId)) return null

  const up = () => { setFormOpen(false); onFeedback(message, true) }
  const down = () => { setSent(false); setFormOpen(true); onFeedback(message, false) }
  const toggleReason = reason => setReasons(rs => rs.includes(reason) ? rs.filter(r => r !== reason) : [...rs, reason])
  const send = () => {
    const comment = [reasons.length ? `Reasons: ${reasons.join(', ')}.` : '', note.trim()].filter(Boolean).join(' ')
    const done = () => { setSent(true); setFormOpen(false) }
    if (!comment) return done()
    onFeedbackComment(message, comment).then(done).catch(() => {})
  }

  const thanks = sent ? 'Thanks — feedback sent.' : message.useful === true ? 'Thanks for the feedback.' : null

  return (
    <>
      <div className="artemis-feedback">
        <span className="artemis-feedback-label">Helpful?</span>
        <button type="button" className={`artemis-icon-button ${message.useful === true ? 'artemis-feedback-up' : ''}`}
          title="Helpful" aria-label="Helpful" aria-pressed={message.useful === true} onClick={up}>
          <Icon name="thumbUp" size={16} />
        </button>
        <button type="button" className={`artemis-icon-button ${message.useful === false ? 'artemis-feedback-down' : ''}`}
          title="Not helpful" aria-label="Not helpful" aria-pressed={message.useful === false} onClick={down}>
          <Icon name="thumbDown" size={16} />
        </button>
        <button type="button" className="artemis-icon-button" title="Copy answer" aria-label="Copy answer" onClick={() => onCopy(message)}>
          <Icon name="copy" size={16} />
        </button>
        {/* Full view only: keeps the answer on the scratchpad as a note. */}
        {onPin && (
          <button type="button" className="artemis-icon-button" title="Pin to scratchpad" aria-label="Pin to scratchpad" onClick={() => onPin(message)}>
            <Icon name="pin" size={16} />
          </button>
        )}
        {thanks && <span className="artemis-feedback-thanks" role="status">{thanks}</span>}
      </div>

      {formOpen && (
        <div className="artemis-feedback-form">
          <span className="artemis-feedback-form-title">What went wrong?</span>
          <div className="artemis-chip-row">
            {FEEDBACK_REASONS.map(reason => (
              <button key={reason} type="button" aria-pressed={reasons.includes(reason)}
                className={`artemis-reason ${reasons.includes(reason) ? 'artemis-reason-on' : ''}`}
                onClick={() => toggleReason(reason)}>
                {reason}
              </button>
            ))}
          </div>
          <textarea className="artemis-feedback-note" rows={2} placeholder="Anything else? (optional)" aria-label="Anything else? (optional)"
            value={note} onChange={e => setNote(e.target.value)} />
          <div className="artemis-feedback-actions">
            <button type="button" className="artemis-small-button" onClick={() => setFormOpen(false)}>Skip</button>
            <button type="button" className="artemis-primary-button" onClick={send}>Send feedback</button>
          </div>
        </div>
      )}
    </>
  )
}

const FollowUps = ({ message, onAsk }) => {
  const questions = Object.values(message.suggestedQuestions || {}).filter(q => typeof q === 'string' && q.trim())
  if (message.status !== 'complete' || questions.length === 0) return null
  return (
    <div className="artemis-follow-ups" aria-label="Suggested follow-ups">
      {questions.map((q, i) => <button key={i} type="button" className="artemis-follow-up" onClick={() => onAsk(q)}>{q}</button>)}
    </div>
  )
}

const Stats = ({ stats, cost }) => {
  const line = statsLine(stats, cost)
  if (!line) return null
  return (
    // Staging only: present when EnableDiagnostics is on.
    <div className="artemis-stats">
      {(line.model || line.modelId) && (
        <div><span>{line.model}</span>{line.modelId && <span className="artemis-stats-dim"> · {line.modelId}</span>}</div>
      )}
      {line.parts.length > 0 && <div>{line.parts.join(' · ')}</div>}
    </div>
  )
}

// The question as its author wrote it. In a group (people: { members, meId }) a partner's message
// sits on the left with their initials and name, and @mentions are highlighted ("@You" for the viewer).
const initialsOf = name => String(name || '?').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
const Question = ({ message, people }) => {
  const segments = people ? mentionSegments(message.question, people.members, people.meId) : null
  const text = segments
    ? segments.map((seg, i) => (seg.mention ? <span key={i} className={`artemis-mention ${seg.me ? 'artemis-mention-me' : ''}`}>{seg.text}</span> : seg.text))
    : message.question
  const mine = !people || message.authorUserId == null || String(message.authorUserId) === String(people.meId)
  if (mine) return <div className="chat-input artemis-question"><p>{text}</p></div>
  const member = people.members.find(m => String(m.UserId) === String(message.authorUserId))
  const name = message.authorName || (member && member.Name) || 'Partner'
  return (
    <div className="artemis-their-message">
      <Avatar url={member && member.AvatarUrl} initials={(member && member.Initials) || initialsOf(name)} name={name} />
      <div className="artemis-their-body">
        <span className="artemis-their-name">{name}</span>
        <p>{text}</p>
      </div>
    </div>
  )
}

const Turn = ({ message, latest: isLatest, now, onAsk, onRetry, onFeedback, onFeedbackComment, onSourceFeedback, onCopy, onToggleLayer, onUpgrade, onShowObject, onRemoveObject, onRefresh, overlay, onToggleView, skillActions, onPin, people, onApplyChange, applyTo, onManageAlert, layers, onOpenPlanner }) => {
  const hasWork = (message.steps || []).length > 0 || (message.tools || []).length > 0

  // A past answer shows folded; its summary opens it in full again (cards, steps, sources).
  const [opened, setOpened] = useState(false)
  const latest = isLatest || opened

  // A group message that didn't mention Artemis has no reply.
  if (message.status === 'posted') {
    return <div className="chat-section artemis-turn artemis-turn-posted"><Question message={message} people={people} /></div>
  }

  if (!latest) {
    return (
      <div className="chat-section artemis-turn artemis-turn-past">
        <Question message={message} people={people} />
        {hasWork && (
          <button type="button" className="artemis-past-summary" onClick={() => setOpened(true)} title="Show the steps and cards">
            {stepsSummary(message, now)}<Icon name="chevronRight" size={14} />
          </button>
        )}
        {message.answer
          ? <Answer message={message} />
          : <p className="artemis-muted">{message.status === 'stopped' ? 'Stopped.' : message.status === 'failed' ? 'Something went wrong' : message.status === 'saving' ? 'Artemis is working on this…' : ''}</p>}
        <div className="artemis-divider" />
      </div>
    )
  }

  return (
    <div className="chat-section artemis-turn">
      <Question message={message} people={people} />
      <AgentSteps message={message} now={now} />
      {message.status === 'streaming' && !message.answer && !hasWork && <span className="artemis-visually-hidden" role="status">Artemis is working</span>}
      {message.answer && <MethodBadges message={message} skillActions={skillActions} people={people} />}
      {/* While a reply waits on ask_user, the questions the server appended to its text (for clients
          without the card) are left out; anything Artemis wrote before asking stays above the card. */}
      {message.answer && (isAsking(message, isLatest)
        ? (lead => lead && <Answer message={{ ...message, answer: lead }} />)(leadIn(message.answer, askQuestions(message)))
        : <Answer message={message} />)}
      {/* The answer is announced once, when it's done, not chunk by chunk. */}
      <div className="artemis-visually-hidden" aria-live="polite">{message.status === 'complete' ? 'Artemis answered.' : ''}</div>
      <MapChanges message={message} onToggleLayer={(action, visible) => onToggleLayer(message, action, visible)} onUpgrade={onUpgrade}
        onApply={onApplyChange} applyTo={applyTo}
        onShowObject={onShowObject} onRemoveObject={object => onRemoveObject(message, object)}
        onToggleView={onToggleView ? (action, undo) => onToggleView(message, action, undo) : null} />
      <ResultCards message={message} now={now} overlay={overlay} onAsk={onAsk} onUpgrade={onUpgrade} skillActions={skillActions} onManageAlert={onManageAlert}
        layers={layers} onOpenPlanner={onOpenPlanner} />
      <Sources message={message} onSourceFeedback={onSourceFeedback} />
      <ReplyState message={message} people={people} onRetry={() => onRetry(message)} onRefresh={onRefresh} />
      <Feedback message={message} onFeedback={onFeedback} onFeedbackComment={onFeedbackComment} onCopy={onCopy} onPin={onPin && message.status === 'complete' && message.answer ? onPin : null} />
      {/* A reply that asked waits for the answer instead of suggesting follow-ups. */}
      {/* A reopened past answer keeps its cards but doesn't ask or suggest again. */}
      {isAsking(message, isLatest)
        ? <AskCard questions={askQuestions(message)} onAsk={onAsk} />
        : isLatest && <FollowUps message={message} onAsk={onAsk} />}
      <Stats stats={message.stats} cost={message.cost} />
    </div>
  )
}

export default Turn
